/**
 * [INPUT]: 依赖 ../scorer 的 Landmark/Pose/midpoint 与本目录 config
 * [OUTPUT]: 对外提供 rollFromLandmarks / yawPitchFromMatrix / composePose
 * [POS]: apps/web/pipeline 的姿态层；roll 用关键点（可推理校验），yaw/pitch 用 MediaPipe 变换矩阵
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 *
 * pitch 来源历史：
 *  - 矩阵（solvePnP）：曾因"轴/符号未校准"怀疑不准。实测正脸读 -7°（轻微低头，合理）。
 *  - 关键点 z 深度法（额头↔下巴 dz）：实测正脸读 +16.9°，有系统性正偏置——MediaPipe 标准脸
 *    模型里下巴 gnathion 的 z 本就比额头靠后，造成 dz>0 的假 pitch。已弃用，回到矩阵。
 *  - 矩阵的轴/符号仍建议首次接通用正脸样例验证近 0；当前量级合理，先采纳。
 */
import { midpoint, type Landmark, type Pose } from '../scorer'

/** Roll：双眼连线与水平线夹角（度）。正脸≈0；robust，可推理验证；任意各向同性空间皆可（尺度无关）。 */
export function rollFromLandmarks(landmarks: Landmark[]): number {
  // 近似瞳孔 = 外/内眦中点（索引取自 MediaPipe 468）
  const leftPupil = midpoint(landmarks[33], landmarks[133])
  const rightPupil = midpoint(landmarks[263], landmarks[362])
  return (Math.atan2(rightPupil.y - leftPupil.y, rightPupil.x - leftPupil.x) * 180) / Math.PI
}

/**
 * 从 MediaPipe facialTransformationMatrixes（4x4 列主序，长度 16）提取 yaw/pitch（度）。
 * 列主序：R[0]=[d0,d4,d8], R[1]=[d1,d5,d9], R[2]=[d2,d6,d10]。轴/符号需浏览器内校准。
 */
export function yawPitchFromMatrix(matrix: ArrayLike<number>): { yaw: number; pitch: number } {
  const r20 = matrix[2]
  const r21 = matrix[6]
  const r22 = matrix[10]
  const pitch = (Math.asin(Math.max(-1, Math.min(1, -r21))) * 180) / Math.PI
  const yaw = (Math.atan2(r20, r22) * 180) / Math.PI
  return { yaw, pitch }
}

/** 组合姿态：roll 来自关键点，yaw/pitch 来自矩阵。矩阵缺失时 yaw/pitch 置 0。 */
export function composePose(landmarks: Landmark[], matrix: ArrayLike<number> | null): Pose {
  const roll = rollFromLandmarks(landmarks)
  if (!matrix || matrix.length < 16) return { yaw: 0, pitch: 0, roll }
  const { yaw, pitch } = yawPitchFromMatrix(matrix)
  return { yaw, pitch, roll }
}